/**
 * `StatusLabel` — a button label that changes without changing the button.
 *
 * The box is always as wide as the WIDEST label, so "Save" → "Saving…" →
 * "Saved" never nudges the buttons beside it. The active label crossfades in
 * place.
 *
 *   <StatusLabel
 *     state={saving ? 'busy' : saved ? 'done' : 'idle'}
 *     labels={{ idle: 'Save', busy: 'Saving…', done: 'Saved ✓' }}
 *   />
 *
 * HOW THE WIDTH IS HELD. Every label is rendered into the same grid cell, but
 * the inactive ones are CSS-generated (`::after { content: attr(data-label) }`)
 * on an empty, invisible span. Generated content sizes the cell exactly like
 * real text and is NOT part of `textContent`, so the button's text — what a
 * test's `hasText`, a screen reader, or `innerText` reads — is only ever the
 * one label that is showing. The first cut kept all labels as real text with
 * `aria-hidden`; the accessible name was right, but `textContent` read
 * "Send inviteSending…" and every text locator on a busy button broke.
 *
 * A label that is not a plain string (an icon beside text) cannot go through
 * `attr()`, so it is rendered as a hidden real node instead; keep such labels
 * to the odd case.
 */
import React from 'react';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { T } from './presets';
import { DISTANCE } from './tokens';

interface StatusLabelProps {
  state: string;
  labels: Record<string, React.ReactNode>;
  className?: string;
  align?: 'center' | 'start';
}

const Box = styled.span<{ $align: 'center' | 'start' }>`
  display: inline-grid;
  justify-items: ${({ $align }) => $align};
  align-items: center;
  vertical-align: middle;

  & > * {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
`;

/* An invisible sizer whose only content is generated — width without text. */
const Sizer = styled.span`
  visibility: hidden;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &::after {
    content: attr(data-label);
  }
`;

const Hidden = styled.span`
  visibility: hidden;
  pointer-events: none;
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const StatusLabel: React.FC<StatusLabelProps> = ({
  state,
  labels,
  className,
  align = 'center',
}) => (
  <Box className={className} $align={align}>
    {Object.entries(labels).map(([key, node]) =>
      typeof node === 'string' ? (
        <Sizer key={key} aria-hidden="true" data-label={node} />
      ) : (
        <Hidden key={key} aria-hidden="true">
          {node}
        </Hidden>
      )
    )}
    {/* Keyed by state so the new label mounts and fades in; the old one is
        removed in the SAME render. No exit half on purpose: a label that
        lingers after a decline is a button whose name still says "Paying" to
        the next click, and to anything reading it. */}
    <motion.span
      key={state}
      initial={{ opacity: 0, y: DISTANCE.micro }}
      animate={{ opacity: 1, y: 0 }}
      transition={T.micro}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
    >
      {labels[state]}
    </motion.span>
  </Box>
);
